/** @jsxImportSource @emotion/react */
import React from "react";
import Form from "react-bootstrap/Form";
import { APGARCalculator } from "./APGARCalculator";
import {
    DRFormGrid, DRFormTitle, DRFormBadge, DRHint,
    DRCheckboxGrid,
    DRAlertContainer, DRAlertCard, DRAlertIcon, DRAlertContent,
    DRAlertTitle, DRAlertDetail, DRAlertAction,
    DRRecordCard,
    DRNoAdmission, DRNoAdmissionIcon, DRNoAdmissionTitle, DRNoAdmissionDesc,
    DRPrimaryBtn, DRSecondaryBtn, DRButtonRow,
} from "../DeliveryRecord/DeliveryRecordStyles";
import Label from "../../../Utils/FormInputs/Label";
import { ResizableTextarea } from "../../../Utils/FormInputs/Textarea";
import SelectInput, { CustomSelect } from "../../../Utils/FormInputs/SelectInput";
import TextField from "../../../Utils/FormInputs/TextField";
import { ReusabelSelect } from "../../../Utils/FormInputs/SelectInput";

/* Helper: convert { value, label } arrays to { value, text } for SelectInput */
const toSelectOptions = (items) => items.map((m) => ({ value: m.value, text: m.label }));

const YES_NO_OPTIONS = [
    { value: "false", text: "No" },
    { value: "true", text: "Yes" },
];

/* Helpers for CustomSelect multi */
const toOptions = (values, optionsList) => {
    if (!values || !values.length) return [];
    return optionsList.filter((o) => values.indexOf(o.value) >= 0);
};

const onMultiChange = (name, handleChange) => (selected) => {
    const values = (selected || []).map((o) => o.value);
    handleChange({ target: { name: name, value: values } });
};

/* Options for multi-select immediate care and vaccinations */
const IMMEDIATE_CARE_OPTIONS = [
    { value: "skin_to_skin_initiated", label: "Skin-to-Skin Contact" },
    { value: "breastfeeding_within_1hr", label: "Breastfeeding within 1 hour" },
    { value: "vitamin_k_given", label: "Vitamin K administered" },
    { value: "eye_prophylaxis_given", label: "Eye prophylaxis (TTC)" },
];

const VACCINATION_OPTIONS = [
    { value: "bcg_given", label: "BCG Vaccine" },
    { value: "opv0_given", label: "OPV-0 (Oral Polio Vaccine)" },
    { value: "hep_b_birth_dose", label: "Hepatitis B Birth Dose" },
    { value: "nevirapine_given", label: "Nevirapine (HIV-exposed)" },
];

/* Convert boolean form fields to selected options for CustomSelect */
const boolFieldsToOptions = (form, options) =>
    options.filter((o) => form[o.value]);

/* Handle multi-select change for boolean form fields — single state update */
const onBoolMultiChange = (setForm, allOptions) => (selected) => {
    const selectedValues = (selected || []).map((o) => o.value);
    setForm((prev) => {
        const next = { ...prev };
        allOptions.forEach((o) => {
            next[o.value] = selectedValues.indexOf(o.value) >= 0;
        });
        return next;
    });
};

const BIRTH_ORDER_OPTIONS = [
    { value: "1", text: "1" },
    { value: "2", text: "2" },
    { value: "3", text: "3" },
    { value: "4", text: "4" },
    { value: "5", text: "5" },
    { value: "6", text: "6" },
];

export const NewbornAssessmentTab = ({ nb, deliveryRecord }) => {

    /* ── No delivery record ── */
    if (!deliveryRecord) {
        return (
            <DRNoAdmission>
                <DRNoAdmissionIcon>👶</DRNoAdmissionIcon>
                <DRNoAdmissionTitle>Delivery Record Required</DRNoAdmissionTitle>
                <DRNoAdmissionDesc>
                    Please save the delivery record first before creating newborn assessments.
                </DRNoAdmissionDesc>
            </DRNoAdmission>
        );
    }

    /* ── Assessment Form ── */
    const renderAssessmentForm = () => {
        const form = nb.form;
        return (
            <div>
                {/* Editing banner */}
                {/* {nb.editingNewborn && (
                    <div style={{
                        padding: "10px 16px",
                        background: "#fff3e0",
                        borderRadius: "8px",
                        border: "1px solid #ffe0b2",
                        marginBottom: "14px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                    }}>
                        <span style={{ fontWeight: 600, color: "#e65100", fontSize: "0.85rem" }}>
                            Editing Baby #{nb.editingNewborn.birth_order || 1} Record
                        </span>
                        <DRSecondaryBtn style={{ padding: "4px 12px", fontSize: "0.78rem" }}
                            onClick={nb.cancelEdit}>
                            Cancel Edit
                        </DRSecondaryBtn>
                    </div>
                )} */}

                {/* Section 1: Basic Info */}
                <DRFormTitle>Newborn Information</DRFormTitle>
                <DRFormGrid cols={4}>
                    <Form.Group>
                        <Label name="Birth Order" />
                        <SelectInput
                            name="birth_order"
                            value={String(form.birth_order || 1)}
                            onChange={nb.handleChange}
                            options={BIRTH_ORDER_OPTIONS}
                        />
                    </Form.Group>
                    <Form.Group>
                        <Label name="Sex" type />
                        <SelectInput
                            name="sex"
                            value={form.sex || ""}
                            onChange={nb.handleChange}
                            options={toSelectOptions(nb.SEX_OPTIONS)}
                            defaultOption="— Select —"
                        />
                    </Form.Group>
                    <Form.Group>
                        <Label name="Birth Weight (grams)" type />
                        <TextField
                            type="number"
                            name="birth_weight_grams"
                            value={form.birth_weight_grams || ""}
                            onChange={nb.handleChange}
                            placeholder="e.g., 3200"
                            min="200"
                            max="7000"
                        />
                        {nb.weightClass && (
                            <DRHint warn={nb.weightClass.color !== "#2e7d32"}>
                                {nb.weightClass.label}
                            </DRHint>
                        )}
                    </Form.Group>
                    <Form.Group>
                        <Label name="Birth Length (cm)" />
                        <TextField
                            type="number"
                            name="birth_length_cm"
                            value={form.birth_length_cm || ""}
                            onChange={nb.handleChange}
                            placeholder="e.g., 50"
                            min="20"
                            max="65"
                        />
                    </Form.Group>
                </DRFormGrid>
                <DRFormGrid cols={3} style={{ marginTop: "10px" }}>
                    <Form.Group>
                        <Label name="Head Circumference (cm)" />
                        <TextField
                            type="number"
                            name="head_circumference_cm"
                            value={form.head_circumference_cm || ""}
                            onChange={nb.handleChange}
                            placeholder="e.g., 35"
                            min="20"
                            max="50"
                        />
                    </Form.Group>
                    <Form.Group>
                        <Label name="Outcome" />
                        <SelectInput
                            name="outcome"
                            value={form.outcome || "alive"}
                            onChange={nb.handleChange}
                            options={toSelectOptions(nb.OUTCOME_OPTIONS)}
                        />
                        {form.outcome !== "alive" && (
                            <DRHint warn>{
                                form.outcome === "fresh_stillbirth" ? "Fresh stillbirth" :
                                form.outcome === "macerated_stillbirth" ? "Macerated stillbirth" :
                                "Neonatal death"
                            }</DRHint>
                        )}
                    </Form.Group>
                    {form.outcome === "neonatal_death" && (
                        <Form.Group>
                            <Label name="Time of Death" />
                            <TextField
                                type="datetime-local"
                                name="death_time"
                                value={form.death_time || ""}
                                onChange={nb.handleChange}
                            />
                        </Form.Group>
                    )}
                </DRFormGrid>

                {/* Section 2: APGAR */}
                <APGARCalculator nb={nb} show10min={form.outcome !== "alive" || (nb.apgarTotals["5min"].complete && nb.apgarTotals["5min"].total < 7)} />

                {/* Section 3: Resuscitation */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "12px", marginTop: "16px" }}>
                    <DRFormTitle>Resuscitation</DRFormTitle>
                    {form.resuscitation_needed
                        ? <DRFormBadge variant="warning">Required</DRFormBadge>
                        : <DRFormBadge variant="success">Not needed</DRFormBadge>}
                </div>
                <DRFormGrid cols={2}>
                    <Form.Group>
                        <Label name="Resuscitation Needed?" />
                        <SelectInput
                            name="resuscitation_needed"
                            value={form.resuscitation_needed ? "true" : "false"}
                            onChange={(ev) => {
                                nb.handleChange({
                                    target: { name: "resuscitation_needed", value: ev.target.value === "true", type: "select" },
                                });
                            }}
                            options={YES_NO_OPTIONS}
                        />
                    </Form.Group>
                    {form.resuscitation_needed && (
                        <Form.Group>
                            <Label name="Type(s) of Resuscitation" />
                            <CustomSelect
                                isMulti
                                options={nb.RESUSCITATION_TYPES}
                                value={toOptions(form.resuscitation_type, nb.RESUSCITATION_TYPES)}
                                onChange={onMultiChange("resuscitation_type", nb.handleChange)}
                                menuPortalTarget={document.body}
                                placeholder="Select types..."
                                closeMenuOnSelect={false}
                            />
                        </Form.Group>
                    )}
                </DRFormGrid>

                {/* Section 4: Immediate Care */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "12px", marginTop: "16px" }}>
                    <DRFormTitle>Immediate Newborn Care</DRFormTitle>
                    <DRFormBadge variant="success">
                        {nb.immediateCareItems.filter((i) => i.checked).length}/{nb.immediateCareItems.length} done
                    </DRFormBadge>
                </div>
                <DRFormGrid cols={3}>
                    <Form.Group>
                        <Label name="Immediate Care Provided" />
                        <CustomSelect
                            isMulti
                            options={IMMEDIATE_CARE_OPTIONS}
                            value={boolFieldsToOptions(form, IMMEDIATE_CARE_OPTIONS)}
                            onChange={onBoolMultiChange(nb.setForm, IMMEDIATE_CARE_OPTIONS)}
                            menuPortalTarget={document.body}
                            placeholder="Select all that apply..."
                            closeMenuOnSelect={false}
                        />
                    </Form.Group>
                    <Form.Group>
                        <Label name="Cord Care" />
                        <SelectInput
                            name="cord_care"
                            value={form.cord_care || ""}
                            onChange={nb.handleChange}
                            options={toSelectOptions(nb.CORD_CARE_OPTIONS)}
                        />
                    </Form.Group>
                    <Form.Group>
                        <Label name="Feeding Option" />
                        <SelectInput
                            name="feeding_option"
                            value={form.feeding_option || ""}
                            onChange={nb.handleChange}
                            options={toSelectOptions(nb.FEEDING_OPTIONS)}
                        />
                    </Form.Group>
                </DRFormGrid>
                {(form.skin_to_skin_initiated || form.breastfeeding_within_1hr) && (
                    <DRFormGrid cols={2} style={{ marginTop: "12px" }}>
                        {form.skin_to_skin_initiated && (
                            <Form.Group>
                                <Label name="Skin-to-Skin Time" />
                                <TextField
                                    type="time"
                                    name="skin_to_skin_time"
                                    value={form.skin_to_skin_time || ""}
                                    onChange={nb.handleChange}
                                />
                            </Form.Group>
                        )}
                        {form.breastfeeding_within_1hr && (
                            <Form.Group>
                                <Label name="Breastfeeding Time" />
                                <TextField
                                    type="time"
                                    name="breastfeeding_time"
                                    value={form.breastfeeding_time || ""}
                                    onChange={nb.handleChange}
                                />
                            </Form.Group>
                        )}
                    </DRFormGrid>
                )}

                {/* Section 5: Birth Vaccinations */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "12px", marginTop: "16px" }}>
                    <DRFormTitle>Birth Vaccinations</DRFormTitle>
                    <DRFormBadge variant="success">
                        {nb.vaccinationItems.filter((i) => i.checked).length}/{nb.vaccinationItems.length} given
                    </DRFormBadge>
                </div>
                <Form.Group>
                    <Label name="Vaccinations Given" />
                    <CustomSelect
                        isMulti
                        options={VACCINATION_OPTIONS}
                        value={boolFieldsToOptions(form, VACCINATION_OPTIONS)}
                        onChange={onBoolMultiChange(nb.setForm, VACCINATION_OPTIONS)}
                        menuPortalTarget={document.body}
                        placeholder="Select all that apply..."
                        closeMenuOnSelect={false}
                    />
                </Form.Group>

                {/* Section 6: Physical Exam Notes */}
                <DRFormTitle style={{ marginTop: "16px", marginBottom: "12px" }}>Physical Examination &amp; Notes</DRFormTitle>
                <DRFormGrid cols={2}>
                    <Form.Group>
                        <Label name="Congenital Anomalies" />
                        <ResizableTextarea
                            name="congenital_anomalies"
                            value={form.congenital_anomalies || ""}
                            onChange={nb.handleChange}
                            rows="2"
                            placeholder="Note any congenital anomalies observed (or 'None')"
                        />
                    </Form.Group>
                    <Form.Group>
                        <Label name="Physical Exam Notes" />
                        <ResizableTextarea
                            name="physical_exam_notes"
                            value={form.physical_exam_notes || ""}
                            onChange={nb.handleChange}
                            rows="2"
                            placeholder="General appearance, activity, tone, cry, color..."
                        />
                    </Form.Group>
                </DRFormGrid>

                {/* Action Buttons */}
                <DRButtonRow>
                    {nb.editingNewborn && (
                        <DRSecondaryBtn onClick={nb.cancelEdit}>Cancel Edit</DRSecondaryBtn>
                    )}
                    <button
                        disabled={nb.saving}
                        onClick={nb.saveNewborn}
                        className="btn sms-info-btn"
                    >
                        {nb.saving ? "Saving..." : (nb.editingNewborn ? "Update Newborn Record" : "Save Newborn Record")}
                    </button>
                </DRButtonRow>
            </div>
        );
    };

    /* ── Helper to format outcome ── */
    const formatOutcome = (outcome) => {
        if (outcome === "alive") return "Live Birth";
        if (outcome === "fresh_stillbirth") return "Fresh Stillbirth";
        if (outcome === "macerated_stillbirth") return "Macerated Stillbirth";
        return "Neonatal Death";
    };

    const formatFeeding = (val) => {
        if (val === "exclusive_breastfeeding") return "Exclusive Breastfeeding";
        if (val === "replacement_feeding") return "Replacement Feeding";
        if (val === "mixed") return "Mixed Feeding";
        return val || "—";
    };

    const formatCordCare = (val) => {
        if (val === "chlorhexidine") return "Chlorhexidine 4%";
        if (val === "dry_care") return "Dry Cord Care";
        return val || "—";
    };

    const formatResuscitationTypes = (types) => {
        if (!types || !types.length) return "—";
        const labels = {
            stimulation: "Stimulation", suction: "Suction",
            bag_mask: "Bag & Mask Ventilation", intubation: "Intubation",
            chest_compressions: "Chest Compressions",
        };
        return types.map((t) => labels[t] || t).join(", ");
    };

    /* ── Summary field cell ── */
    const SummaryField = ({ label, children, warn }) => (
        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <span style={{ fontSize: "0.75rem", color: "#888", fontWeight: 500 }}>{label}</span>
            <span style={{ fontSize: "0.85rem", fontWeight: 600, color: warn ? "#c62828" : "#333" }}>{children}</span>
        </div>
    );

    /* ── Recorded Newborn Summary ── */
    const renderRecordedSummary = () => (
        <div>
            {nb.newborns.map((baby, idx) => {
                const immediateCare = [];
                if (baby.skin_to_skin_initiated) immediateCare.push("Skin-to-Skin Contact");
                if (baby.breastfeeding_within_1hr) immediateCare.push("Breastfeeding within 1hr");
                if (baby.vitamin_k_given) immediateCare.push("Vitamin K");
                if (baby.eye_prophylaxis_given) immediateCare.push("Eye Prophylaxis (TTC)");

                const vaccinations = [];
                if (baby.bcg_given) vaccinations.push("BCG");
                if (baby.opv0_given) vaccinations.push("OPV-0");
                if (baby.hep_b_birth_dose) vaccinations.push("Hepatitis B");
                if (baby.nevirapine_given) vaccinations.push("Nevirapine");

                return (
                    <DRRecordCard key={baby.id || idx} style={{ marginBottom: "16px" }}>
                        {/* Header */}
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "14px", borderBottom: "1px solid #e8eaf6", paddingBottom: "10px" }}>
                            <h5 style={{ margin: 0, fontSize: "1rem", fontWeight: 700, color: "#283593" }}>
                                Baby #{baby.birth_order || (idx + 1)} — {baby.sex === "male" ? "Male" : baby.sex === "female" ? "Female" : "Ambiguous"}
                            </h5>
                            <DRSecondaryBtn
                                style={{ padding: "4px 14px", fontSize: "0.78rem" }}
                                onClick={() => nb.startEditNewborn(baby)}
                            >
                                Edit
                            </DRSecondaryBtn>
                        </div>

                        {/* Basic Info */}
                        <DRFormTitle style={{ fontSize: "0.85rem", marginBottom: "8px" }}>Basic Information</DRFormTitle>
                        <DRFormGrid cols={4} style={{ marginBottom: "16px" }}>
                            <SummaryField label="Birth Weight" warn={parseFloat(baby.birth_weight_grams) < 2500}>
                                {baby.birth_weight_grams || "—"}g
                            </SummaryField>
                            <SummaryField label="Birth Length">
                                {baby.birth_length_cm ? baby.birth_length_cm + " cm" : "—"}
                            </SummaryField>
                            <SummaryField label="Head Circumference">
                                {baby.head_circumference_cm ? baby.head_circumference_cm + " cm" : "—"}
                            </SummaryField>
                            <SummaryField label="Outcome" warn={baby.outcome !== "alive"}>
                                {formatOutcome(baby.outcome)}
                            </SummaryField>
                        </DRFormGrid>

                        {/* APGAR Scores */}
                        <DRFormTitle style={{ fontSize: "0.85rem", marginBottom: "8px" }}>APGAR Scores</DRFormTitle>
                        <DRFormGrid cols={3} style={{ marginBottom: "16px" }}>
                            <SummaryField label="1 Minute" warn={baby.apgar_1min !== null && baby.apgar_1min < 7}>
                                {baby.apgar_1min !== null ? baby.apgar_1min + "/10" : "—"}
                            </SummaryField>
                            <SummaryField label="5 Minutes" warn={baby.apgar_5min !== null && baby.apgar_5min < 7}>
                                {baby.apgar_5min !== null ? baby.apgar_5min + "/10" : "—"}
                            </SummaryField>
                            <SummaryField label="10 Minutes">
                                {baby.apgar_10min !== null ? baby.apgar_10min + "/10" : "—"}
                            </SummaryField>
                        </DRFormGrid>

                        {/* Resuscitation */}
                        <DRFormTitle style={{ fontSize: "0.85rem", marginBottom: "8px" }}>Resuscitation</DRFormTitle>
                        <DRFormGrid cols={2} style={{ marginBottom: "16px" }}>
                            <SummaryField label="Resuscitation Needed">
                                {baby.resuscitation_needed ? "Yes" : "No"}
                            </SummaryField>
                            {baby.resuscitation_needed && (
                                <SummaryField label="Type(s)">
                                    {formatResuscitationTypes(baby.resuscitation_type)}
                                </SummaryField>
                            )}
                        </DRFormGrid>

                        {/* Immediate Care */}
                        <DRFormTitle style={{ fontSize: "0.85rem", marginBottom: "8px" }}>Immediate Newborn Care</DRFormTitle>
                        <DRFormGrid cols={3} style={{ marginBottom: "16px" }}>
                            <SummaryField label="Care Provided">
                                {immediateCare.length > 0 ? immediateCare.join(", ") : "None"}
                            </SummaryField>
                            <SummaryField label="Cord Care">
                                {formatCordCare(baby.cord_care)}
                            </SummaryField>
                            <SummaryField label="Feeding Option">
                                {formatFeeding(baby.feeding_option)}
                            </SummaryField>
                        </DRFormGrid>
                        {(baby.skin_to_skin_time || baby.breastfeeding_time) && (
                            <DRFormGrid cols={2} style={{ marginBottom: "16px" }}>
                                {baby.skin_to_skin_time && (
                                    <SummaryField label="Skin-to-Skin Time">{baby.skin_to_skin_time}</SummaryField>
                                )}
                                {baby.breastfeeding_time && (
                                    <SummaryField label="Breastfeeding Time">{baby.breastfeeding_time}</SummaryField>
                                )}
                            </DRFormGrid>
                        )}

                        {/* Vaccinations */}
                        <DRFormTitle style={{ fontSize: "0.85rem", marginBottom: "8px" }}>Birth Vaccinations</DRFormTitle>
                        <div style={{ marginBottom: "16px" }}>
                            <SummaryField label="Vaccinations Given">
                                {vaccinations.length > 0 ? vaccinations.join(", ") : "None given"}
                            </SummaryField>
                        </div>

                        {/* Physical Exam & Notes */}
                        {(baby.congenital_anomalies || baby.physical_exam_notes) && (
                            <>
                                <DRFormTitle style={{ fontSize: "0.85rem", marginBottom: "8px" }}>Physical Examination &amp; Notes</DRFormTitle>
                                <DRFormGrid cols={2} style={{ marginBottom: "8px" }}>
                                    {baby.congenital_anomalies && (
                                        <SummaryField label="Congenital Anomalies">{baby.congenital_anomalies}</SummaryField>
                                    )}
                                    {baby.physical_exam_notes && (
                                        <SummaryField label="Physical Exam Notes">{baby.physical_exam_notes}</SummaryField>
                                    )}
                                </DRFormGrid>
                            </>
                        )}

                        {/* Death time if applicable */}
                        {baby.outcome === "neonatal_death" && baby.death_time && (
                            <div style={{ marginTop: "8px" }}>
                                <SummaryField label="Time of Death" warn>
                                    {new Date(baby.death_time).toLocaleString()}
                                </SummaryField>
                            </div>
                        )}
                    </DRRecordCard>
                );
            })}
        </div>
    );

    const expectedBabies = parseInt(deliveryRecord.number_of_babies) || 1;
    const allRecorded = nb.newborns.length >= expectedBabies;
    const isEditing = !!nb.editingNewborn;
    const showForm = isEditing || !allRecorded;

    return (
        <div>
            {/* Alerts */}
            {nb.alerts.length > 0 && (
                <DRAlertContainer>
                    {nb.alerts.map((alert, idx) => (
                        <DRAlertCard key={idx} severity={alert.type}>
                            <DRAlertIcon>{alert.type === "critical" ? "🚨" : "⚠️"}</DRAlertIcon>
                            <DRAlertContent>
                                <DRAlertTitle>{alert.title}</DRAlertTitle>
                                <DRAlertDetail>{alert.detail}</DRAlertDetail>
                                <DRAlertAction>{alert.action}</DRAlertAction>
                            </DRAlertContent>
                        </DRAlertCard>
                    ))}
                </DRAlertContainer>
            )}

            {/* Show recorded summaries if any exist */}
            {nb.newborns.length > 0 && !isEditing && renderRecordedSummary()}

            {/* Show form if still recording or editing */}
            {showForm && renderAssessmentForm()}

            {/* All recorded — no form, just summaries shown above */}
            {allRecorded && !isEditing && (
                <div style={{ textAlign: "center", padding: "12px 0", color: "#2e7d32", fontSize: "0.85rem", fontWeight: 600 }}>
                    All {expectedBabies} {expectedBabies === 1 ? "baby" : "babies"} recorded.
                </div>
            )}
        </div>
    );
};
